/**
 * Central registry of every CMS-editable image slot.
 *
 * Slots are generated in two ways:
 *   1. Static slots — declared inline for hand-picked images (hero, philosophy,
 *      founder, page heroes, global assets).
 *   2. Data-driven slots — derived from the content.js arrays (Showcase, Services,
 *      Celebrations, Portfolio, Blog covers) so every item gets a slot automatically.
 *
 * Adding a new editable image:
 *   - For one-offs: append a new entry to STATIC_SLOTS below.
 *   - For arrays in content.js: just add the item — it appears in admin automatically.
 *   - In JSX: <Img imageKey="your.key" defaultSrc={...} defaultAlt={...} />
 */

import {
  BRAND, HERO_IMAGES, SHOWCASE_CATEGORIES, SERVICES, CELEBRATION_TYPES,
  PORTFOLIO_GRID, BLOGS,
} from "./content";

export const IMAGE_CATEGORIES = [
  "Homepage",
  "About",
  "Services",
  "Celebrations",
  "Venues",
  "Portfolio",
  "Packages",
  "Process",
  "Checklist",
  "Blogs",
  "Contact",
  "Founders",
  "Partners",
  "Global Assets",
];

const slug = (s = "") =>
  s.toString().trim().toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// ----- Static / hand-picked slots -----
const STATIC_SLOTS = [
  // Global assets
  { key: "global.logo", category: "Global Assets", label: "Brand logo (header, colored)", defaultUrl: BRAND.logo, defaultAlt: "Petals & Promises logo" },
  { key: "global.logo-light", category: "Global Assets", label: "Brand logo (footer, white)", defaultUrl: BRAND.logoLight, defaultAlt: "Petals & Promises white logo" },
  { key: "global.favicon", category: "Global Assets", label: "Favicon (browser tab)", defaultUrl: BRAND.favicon, defaultAlt: "Petals & Promises favicon" },
  { key: "global.og-image", category: "Global Assets", label: "Social share / Open Graph image", defaultUrl: BRAND.logo, defaultAlt: "Petals & Promises" },

  // Hero slides (HERO_IMAGES array)
  ...HERO_IMAGES.map((src, i) => ({
    key: `home.hero.${i + 1}`,
    category: "Homepage",
    label: `Hero slide · ${i + 1}`,
    defaultUrl: src,
    defaultAlt: `Petals & Promises hero — slide ${i + 1}`,
  })),

  { key: "home.philosophy", category: "Homepage", label: "Philosophy section image", defaultUrl: "https://images.unsplash.com/photo-1604017011826-d3b4c23f8914?auto=format&fit=crop&w=1400&q=80", defaultAlt: "Petals & Promises Indian wedding decor styling" },

  // About page
  { key: "about.gallery.1", category: "About", label: "About gallery · 1", defaultUrl: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=900&q=80", defaultAlt: "Indian wedding bride and groom varmala moment" },
  { key: "about.gallery.2", category: "About", label: "About gallery · 2", defaultUrl: "https://images.unsplash.com/photo-1604017011826-d3b4c23f8914?auto=format&fit=crop&w=900&q=80", defaultAlt: "Indian wedding mandap floral decor" },
  { key: "about.gallery.3", category: "About", label: "About gallery · 3", defaultUrl: "https://images.unsplash.com/photo-1758307390525-46a0825d4e50?auto=format&fit=crop&w=900&q=80", defaultAlt: "Premium Indian celebration tablescape and styling" },
  { key: "about.gallery.4", category: "About", label: "About gallery · 4", defaultUrl: "https://images.unsplash.com/photo-1530023367847-a683933f4172?auto=format&fit=crop&w=900&q=80", defaultAlt: "Luxury Indian wedding decor and tablescape" },

  // Founders
  { key: "founder.shubham", category: "Founders", label: "Shubham Kalra · Portrait", defaultUrl: "https://customer-assets.emergentagent.com/job_premium-celebrations-14/artifacts/qblmgezx_ChatGPT%20Image%20May%2010%2C%202026%2C%2006_37_10%20AM.png", defaultAlt: "Shubham Kalra, Founder & Creative Lead" },

  // Partners (logos for the marquee — defaults are wordmarks so URL is optional)
  { key: "partner.taj", category: "Partners", label: "Partner logo · Taj", defaultUrl: "", defaultAlt: "Taj Hotels logo" },
  { key: "partner.oberoi", category: "Partners", label: "Partner logo · Oberoi", defaultUrl: "", defaultAlt: "Oberoi Hotels logo" },
  { key: "partner.marriott", category: "Partners", label: "Partner logo · Marriott", defaultUrl: "", defaultAlt: "Marriott logo" },
  { key: "partner.hyatt", category: "Partners", label: "Partner logo · Hyatt", defaultUrl: "", defaultAlt: "Hyatt logo" },
  { key: "partner.itc", category: "Partners", label: "Partner logo · ITC Hotels", defaultUrl: "", defaultAlt: "ITC Hotels logo" },
  { key: "partner.leela", category: "Partners", label: "Partner logo · The Leela", defaultUrl: "", defaultAlt: "The Leela logo" },
  { key: "partner.radisson", category: "Partners", label: "Partner logo · Radisson", defaultUrl: "", defaultAlt: "Radisson logo" },
  { key: "partner.hilton", category: "Partners", label: "Partner logo · Hilton", defaultUrl: "", defaultAlt: "Hilton logo" },
  { key: "partner.accor", category: "Partners", label: "Partner logo · Accor", defaultUrl: "", defaultAlt: "Accor logo" },
  { key: "partner.westin", category: "Partners", label: "Partner logo · Westin", defaultUrl: "", defaultAlt: "Westin logo" },
  { key: "partner.fairmont", category: "Partners", label: "Partner logo · Fairmont", defaultUrl: "", defaultAlt: "Fairmont logo" },
  { key: "partner.roseate", category: "Partners", label: "Partner logo · Roseate", defaultUrl: "", defaultAlt: "Roseate logo" },

  // Packages — pure CTAs today, but slot reserved for future card images
  { key: "package.brochure-thumb", category: "Packages", label: "Brochure / deck thumbnail", defaultUrl: "", defaultAlt: "Petals & Promises brochure preview" },
];

// ----- Data-driven slots from content.js arrays -----
const SHOWCASE_SLOTS = SHOWCASE_CATEGORIES.map((s) => ({
  key: `home.showcase.${slug(s.title)}`,
  category: "Homepage",
  label: `Showcase · ${s.title}`,
  defaultUrl: s.image,
  defaultAlt: `${s.title} — Petals & Promises`,
}));

const SERVICE_SLOTS = SERVICES.map((s) => ({
  key: `service.${s.slug}`,
  category: "Services",
  label: `Service · ${s.title}`,
  defaultUrl: s.image,
  defaultAlt: `${s.title} service — Petals & Promises`,
}));

const CELEBRATION_SLOTS = CELEBRATION_TYPES.map((c) => ({
  key: `celebration.${slug(c.name)}`,
  category: "Celebrations",
  label: `Celebration · ${c.name}`,
  defaultUrl: c.image,
  defaultAlt: `${c.name} — Petals & Promises`,
}));

const PORTFOLIO_SLOTS = PORTFOLIO_GRID.map((p, i) => ({
  key: `portfolio.${slug(p.title)}-${i + 1}`,
  category: "Portfolio",
  label: `Portfolio · ${p.title}${p.location ? ` (${p.location})` : ""}`,
  defaultUrl: p.image,
  defaultAlt: `${p.title} — ${p.category}`,
}));

const BLOG_SLOTS = BLOGS.flatMap((b) => {
  const cover = [{
    key: `blog.cover.${b.slug}`,
    category: "Blogs",
    label: `Blog cover · ${b.title}`,
    defaultUrl: b.image,
    defaultAlt: `${b.title} — cover`,
  }];
  const inline = (b.images || []).map((src, i) => ({
    key: `blog.inline.${b.slug}.${i + 1}`,
    category: "Blogs",
    label: `Blog inline · ${b.title} — image ${i + 1}`,
    defaultUrl: src,
    defaultAlt: `${b.title} — supporting visual ${i + 1}`,
  }));
  return [...cover, ...inline];
});

export const IMAGE_SLOTS = [
  ...STATIC_SLOTS,
  ...SHOWCASE_SLOTS,
  ...SERVICE_SLOTS,
  ...CELEBRATION_SLOTS,
  ...PORTFOLIO_SLOTS,
  ...BLOG_SLOTS,
];

export const SLOT_MAP = Object.fromEntries(IMAGE_SLOTS.map((s) => [s.key, s]));

// Helper used by JSX to build a stable key from an item's name/title/slug
export const slotKeyFor = {
  showcase: (item) => `home.showcase.${slug(item.title)}`,
  service: (item) => `service.${item.slug}`,
  celebration: (item) => `celebration.${slug(item.name)}`,
  portfolio: (item, index) => `portfolio.${slug(item.title)}-${index + 1}`,
  blogCover: (item) => `blog.cover.${item.slug}`,
  blogInline: (item, index) => `blog.inline.${item.slug}.${index + 1}`,
  partner: (textOrSlug) => `partner.${slug(textOrSlug)}`,
};
